import React, { useMemo, useState } from "react";
import { Tab } from "@headlessui/react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopCard from "./TopCard";
import Introduction from "./Introduction";
import VoiceCard from "./VoiceCard";
import ResultCard from "./components/ResultCard";
import Strength from "./Strength";
import CareerPath from "./CareerPath";
import Habit from "./Habit";
import Relationship from "./Relationship";
import Conclusion from "./Conclusion";
import ExploreCard from "../../Components/DetailResultPage/ExploreCard";
import NavigationButtons from "./components/NavigationButtons";
import LearningStyle from "./LearningStyle";
import AppointmentCard from "../../Components/DetailResultPage/AppointmentCard";
import { cn } from "../../utils/twMerge";

const DetailedResultContent = ({
  data,
  images,
  resultOverride,
  showAppointmentCard = true,
  className,
}) => {
  const { t } = useTranslation("result");
  const [searchParams] = useSearchParams();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const path = searchParams.get("path") ?? localStorage.getItem("path");

  const categories = useMemo(() => {
    const habitCategory =
      path !== "student"
        ? { category: t("nav.workplaceHabit"), content: <Habit /> }
        : { category: t("nav.academicHabit"), content: <Habit /> };

    const fourthCategory =
      path !== "student"
        ? { category: t("nav.yourRelationships"), content: <Relationship /> }
        : {
            category: t("nav.yourLearningStyle"),
            content: <LearningStyle />,
          };

    return [
      {
        category: t("nav.introduction"),
        content: <Introduction resultOverride={resultOverride} />,
      },
      { category: t("nav.strengthChallenges"), content: <Strength /> },
      {
        category: t("nav.careerPath"),
        content: <CareerPath resultOverride={resultOverride} />,
      },
      habitCategory,
      fourthCategory,
      { category: t("nav.conclusion"), content: <Conclusion /> },
    ];
  }, [path, resultOverride, t]);

  return (
    <div className={cn("bg-[#F9FAFC] z-10", className)}>
      <TopCard data={data} images={images} />

      <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
        <div className="p-[1px] rounded-2xl bg-gradient-to-r max-w-[90%] mx-auto relative z-10 from-[#3D74C0]/10 via-[#3D74C0] to-[#3D74C0]/10">
          <Tab.List className="flex gap-4 rounded-2xl bg-[#F2F7FF] py-6 md-max:py-4 px-4 w-full overflow-x-scroll no-scrollbar">
            {categories.map((category, idx) => (
              <Tab
                key={`${category.category}-${idx}`}
                className={({ selected }) =>
                  cn(
                    "rounded-lg text-xl md-max:text-sm font-normal text-[#9D9D9D] w-full text-nowrap",
                    selected
                      ? "text-primary-blue underline font-bold outline-none"
                      : ""
                  )
                }
              >
                {category.category}
              </Tab>
            ))}
          </Tab.List>
        </div>
        <Tab.Panels className="mt-20 md-max:mt-10">
          <VoiceCard data={data} />
          {categories.map((category, idx) => (
            <Tab.Panel key={idx}>
              {category.category !== t("nav.introduction") && (
                <ResultCard resultOverride={resultOverride} />
              )}
              {category.content}
              <div className="py-20 px-[10rem] md-max:py-4 md-max:px-[1rem]">
                <ExploreCard category={category.category} />
                {showAppointmentCard && <AppointmentCard />}
                <NavigationButtons setSelectedIndex={setSelectedIndex} />
              </div>
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default DetailedResultContent;
